import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import lodashKeyBy from 'lodash/fp/keyBy';
import { useMediaQuery } from 'react-responsive';
import { getListingAppliances } from 'common/listingHelpers';

import { MEDIA_QUERY_MEDIUM_SCREEN } from 'client/constants/mediaQueries';

import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';

const SrPartnerListingApplianceIncluded = ({
  coverPhoto,
  enabled,
  label,
  submittedDate,
}) => {

  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN,
  });

  return (
    <SrBox
      borderRadius='14px'
      boxShadow='0px 5px 21px rgba(25, 51, 64, 0.05)'
      backgroundColor='white'
      overflow='hidden'
    >
      <SrBox
        width='100%'
        height={isMediumScreen ? '44.5vw' : '320px'}
        backgroundImage={coverPhoto ? `url("${coverPhoto}")` : null}
        backgroundSize='cover'
        backgroundPosition='center'
        position='relative'
      >
        {!enabled && (
          <SrBox
            position='absolute'
            top='0'
            left='0'
            width='100%'
            height='100%'
            backgroundColor={coverPhoto ? 'rgba(133, 133, 133, 0.8)' : '#E5E6E7'}
          >
            <SrFlex height='100%' align='center' justify='center'>
              <SrText
                color='white'
                size='2xl'
                weight='bolder'
                align='center'
              >
                {`No ${label}`}
              </SrText>
            </SrFlex>
          </SrBox>
        )}
        {enabled && !coverPhoto && (
          <SrBox
            position='absolute'
            top='0'
            left='0'
            width='100%'
            height='100%'
            backgroundColor='rgba(0, 162, 56, 0.25)'
          >
            <SrFlex height='100%' align='center' justify='center'>
              <SrText
                color='white'
                size='2xl'
                weight='bolder'
              >
                Photos not available
              </SrText>
            </SrFlex>
          </SrBox>
        )}
      </SrBox>
      <SrBox padding='15px 15px 15px 15px'>
        <SrBox
          overflow='hidden'
          textOverflow='ellipsis'
          maxWidth='100%'
        >
          <SrText
            weight='800'
            fontSize='16px'
            lineHeight='19px'
            color='blueGreycliff'
            font='greycliff'
            whiteSpace='nowrap'
          >
            {`${label} `}
          </SrText>
        </SrBox>
        {submittedDate && (
          <SrBox marginTop='8px'>
            <SrText
              display='block'
              weight='500'
              fontSize='13px'
              lineHeight='16px'
              color='blueGreycliff'
              font='greycliff'
            >
              {moment(submittedDate).format('MMM D [at] h:mm A')}
            </SrText>
          </SrBox>
        )}
      </SrBox>
    </SrBox>
  );
};

SrPartnerListingApplianceIncluded.propTypes = {
  label: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  submittedDate: PropTypes.string,
  coverPhoto: PropTypes.string,
};

SrPartnerListingApplianceIncluded.displayName = 'SrPartnerListingApplianceIncluded';

const SrPartnerListingAppliancesIncludedSection = ({ listing, onEditClick }) => {
  const appliances = getListingAppliances(listing).map((applianceProps) => {
    const hidden = (
      typeof applianceProps.enabled !== 'boolean'
      || !applianceProps.enabled
      || !applianceProps.submittedDate
    );
    return {
      ...applianceProps,
      enabled: hidden ? false : applianceProps.enabled,
      key: applianceProps.label,
      onEditClick: onEditClick,
      hidden: hidden,
    };
  });

  const appliancesByLabel = lodashKeyBy('label', appliances);

  if (appliancesByLabel['Electric Stove'].enabled && !appliancesByLabel['Gas Stove'].enabled) {
    appliancesByLabel['Gas Stove'].hidden = true;
  }

  if (appliancesByLabel['Gas Stove'].enabled && !appliancesByLabel['Electric Stove'].enabled) {
    appliancesByLabel['Electric Stove'].hidden = true;
  }

  if (appliancesByLabel.Washer.enabled || appliancesByLabel.Dryer.enabled) {
    appliancesByLabel['Washer/Dryer Connections'].hidden = true;
  }

  if (!appliancesByLabel['Electric Stove'].enabled && !appliancesByLabel['Gas Stove'].enabled) {
    appliancesByLabel['Electric Stove'].hidden = true;
    appliancesByLabel['Gas Stove'].hidden = true;
  }

  const appliancesToRender = appliances.filter(({ hidden }) => !hidden);

  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN,
  });

  return (
    <SrGridRow
      columns={isMediumScreen ? 2 : 3}
      maxWidth='100%'
      columnWrapMobile={false}
      columnGap='15px'
      rowGap='15px'
    >
      {appliancesToRender.map((applianceProps) => (
        <SrPartnerListingApplianceIncluded
          {...applianceProps}
        />
      ))}
      {appliancesToRender.length === 0 && (
        <SrBox marginLeft={isMediumScreen ? '14px' : null}>
          <SrText
            font='greycliff'
            whiteSpace='nowrap'
          >
            No appliance data collected yet.
          </SrText>
        </SrBox>
      )}
    </SrGridRow>
  );
};

SrPartnerListingAppliancesIncludedSection.propTypes = {
  listing: PropTypes.object.isRequired,
  onEditClick: PropTypes.func,
  onListingPhotosLinkClick: PropTypes.func.isRequired,
};

SrPartnerListingAppliancesIncludedSection.displayName = 'SrPartnerListingAppliancesIncludedSection';

export default SrPartnerListingAppliancesIncludedSection;
